import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ChevronRight, X } from 'lucide-react'
import { useAuth } from '@/components/auth/AuthContext'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { PageHeaderIcon } from './PageHeaderIcon'
import { QueueIcon, queueIconColor, queueIconMeaningForNotification } from '@/components/QueueIcon'
import { useNotifications } from '@/modules/notifications/hooks'
import { api } from '@/lib/api'
import { NUMBER_SUPPORT_EMAIL, NOTIFICATION_CATALOG_BY_KEY, type AccountProcessingStatus } from '@tenfour/contracts'
import { notificationHref, systemNotifications } from './system-notifications'
import s from './ProcessingStatus.module.css'

// The tray's exit mirrors its 240ms entrance; keep in step with `--dur-med`
// on `.panelClosing`.
const EXIT_MS = 240

/** Non-modal tray: normal Tab order, Escape restores focus, outside input dismisses. */
function useNotificationTray() {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen((value) => !value), [])
  const closeAndFocus = useCallback(() => { close(); trigger.current?.focus() }, [close])
  useEffect(() => {
    if (!open) return
    const outside = (event: Event) => { if (!root.current?.contains(event.target as Node)) close() }
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); closeAndFocus() } }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    document.addEventListener('keydown', key)
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside); document.removeEventListener('keydown', key) }
  }, [open, close, closeAndFocus])
  return { open, root, trigger, close, toggle, closeAndFocus }
}

export function ProcessingStatus() {
  const { accountId } = useAuth()
  const tray = useNotificationTray()
  const id = useId()
  // A closed tray stays mounted, inert, for its exit (focus is already back on
  // the bell); reopening mid-exit brings the same panel back. Reduced motion
  // takes it away at once.
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [leaving, setLeaving] = useState(false)
  const [wasOpen, setWasOpen] = useState(tray.open)
  if (tray.open !== wasOpen) {
    setWasOpen(tray.open)
    setLeaving(!tray.open)
  }
  useEffect(() => {
    if (!leaving) return undefined
    const timer = window.setTimeout(() => setLeaving(false), reduced ? 0 : EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [leaving, reduced])
  const status = useQuery({ queryKey: ['account-processing', accountId],
    queryFn: ({ signal }) => api<AccountProcessingStatus>(`/accounts/${accountId}/processing`, { signal }),
    enabled: !!accountId, staleTime: 4000, refetchInterval: 5000,
  })
  const notifications = useNotifications(accountId ?? '', { refetchInterval: 30000 })
  const items = systemNotifications(status.data)
  const recent = (notifications.data ?? []).slice(0, 6)
  const attention = items.some((item) => item.attention) || status.isError || notifications.isError
  const unread = (notifications.data ?? []).some((item) => !item.read)
  const hasUpdates = attention || unread || items.length > 0
  const refresh = useCallback(() => { void status.refetch(); void notifications.refetch() }, [status.refetch, notifications.refetch])

  return <div className={s.status} ref={tray.root}>
      <button type="button" ref={tray.trigger} className={s.trigger} aria-label={hasUpdates ? 'Notifications, new updates' : 'Notifications'} aria-expanded={tray.open} aria-controls={id} onClick={tray.toggle}>
        {/* Notifications shares the navigation family's monochrome Reminders bell. */}
        <PageHeaderIcon pathname="/reminders" state={tray.open ? 'active' : 'idle'} className={s.bell} /><span className={s.label}>Notifications</span>
        {hasUpdates && <span className={`${s.dot} ${attention ? s.attention : ''}`} aria-hidden />}
      </button>
      {(tray.open || leaving) && <section id={id} className={tray.open ? s.panel : `${s.panel} ${s.panelClosing}`} aria-label="Notifications" {...(tray.open ? {} : { inert: '' as const })}>
        <header className={s.heading}><h2>Notifications</h2><button type="button" className={s.close} aria-label="Close notifications" onClick={tray.closeAndFocus}><X size={18} aria-hidden /><span className={s.tip} role="tooltip">Close notifications</span></button></header>
        <div className={s.body}>
          {(status.isError || notifications.isError) && <div className={s.feedback} role="status"><p>Couldn’t load {status.isError && notifications.isError ? 'your updates' : status.isError ? 'setup progress' : 'recent activity'}. Loaded updates are still shown.</p><button type="button" onClick={refresh}>Try again</button></div>}
          {(status.isPending || notifications.isPending) && <p className={s.feedback} role="status">Checking for updates…</p>}
          {items.length > 0 && <section aria-label="Setup and background work"><h3 className={s.groupLabel}>Setup and background work</h3><ul className={s.items}>{items.map((item) => <li key={item.id}>
            <Link className={s.item} to={item.href ?? '/notifications'} onClick={tray.close}>
              <span className={s.mark}><span className={item.busy ? s.spinner : `${s.stateDot} ${item.attention ? s.attention : ''}`} aria-hidden /></span>
              <span className={s.itemText}><strong>{item.title}</strong><small>{item.detail}</small></span><ChevronRight className={s.arrow} aria-hidden />
            </Link>
          </li>)}</ul></section>}
          {recent.length > 0 && <section aria-label="Recent activity"><h3 className={s.groupLabel}>Recent activity</h3><ul className={s.items}>{recent.map((item) => <li key={item.id}>
            <Link className={s.item} to={notificationHref(item)} onClick={tray.close}>
              <QueueIcon meaning={queueIconMeaningForNotification(item.entityType, item.eventType)} color={queueIconColor(item.id)} className={s.mark} />
              <span className={s.itemText}><strong>{NOTIFICATION_CATALOG_BY_KEY[item.eventType]?.label ?? item.title}</strong><small><time dateTime={item.createdAt}>{new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time></small></span>
              {!item.read && <span className={s.stateDot} aria-label="Unread" />}<ChevronRight className={s.arrow} aria-hidden />
            </Link>
          </li>)}</ul></section>}
          {!items.length && !recent.length && !status.isPending && !notifications.isPending && !status.isError && !notifications.isError && <div className={s.empty}><PageHeaderIcon pathname="/reminders" state="idle" className={s.bell} /><strong>You’re all caught up</strong><p>System updates and setup progress will appear here.</p></div>}
        </div>
        <footer className={s.footer}><Link to="/notifications" onClick={tray.close}>View all activity <ChevronRight size={14} aria-hidden /></Link><a href={`mailto:${NUMBER_SUPPORT_EMAIL}`}>Contact support</a></footer>
      </section>}
    </div>
}
